import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Video, FileText, CheckCircle2 } from 'lucide-react';
import { marked } from 'marked';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useFont } from '../contexts/FontContext';
import { useSubscription } from '../hooks/useSubscription';
import { api } from '../services/api';
import {
  getCourseContent,
  getModules,
  getSubModules,
  getModuleCourses,
} from '../mockApi';
import { useInView } from 'react-intersection-observer';
import type { CourseContent, CourseProgress } from '../types';
import CourseComments from '../components/CourseComments';

export default function CourseContent() {
  /* ------------------------------------------------------------------ */
  /* hooks & context                                                    */
  /* ------------------------------------------------------------------ */
  const { code, courseId }  = useParams();
  const navigate            = useNavigate();
  const { theme }           = useTheme();
  const { font }            = useFont();
  const { updateUserState } = useAuth();
  const { hasActiveSubscription } = useSubscription();

  /* ------------------------------------------------------------------ */
  /* state                                                              */
  /* ------------------------------------------------------------------ */
  const [content,  setContent]  = useState<CourseContent | null>(null);
  const [progress, setProgress] = useState<CourseProgress | null>(null);
  const [courseData, setCourseData] = useState<any>(null);

  /* ------------------------------------------------------------------ */
  /* refs & intersection sentinel                                       */
  /* ------------------------------------------------------------------ */
  const savingRef = useRef(false);
  const { ref: bottomRef, inView: bottomInView } = useInView({
    threshold  : 0,
    rootMargin : '0px 0px -150px 0px',
    triggerOnce: true,
  });

  /* ------------------------------------------------------------------ */
  /* utils                                                              */
  /* ------------------------------------------------------------------ */
  const slugify = (text: string) =>
    text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

  const normaliseProgress = (
    courseId: string,
    value: number | CourseProgress | null | undefined
  ): CourseProgress => {
    if (value && typeof value === 'object') {
      return {
        courseId,
        completed: Boolean((value as any).completed ?? value.progress === 100),
        progress : (value as any).progress ?? ((value as any).completed ? 100 : 0),
        lastAccessedAt:
          (value as any).lastAccessedAt || new Date().toISOString(),
      };
    }

    return {
      courseId,
      completed     : value === 100,
      progress      : value === 100 ? 100 : 0,
      lastAccessedAt: new Date().toISOString(),
    };
  };

  /* ------------------------------------------------------------------ */
  /* mark course as completed                                           */
  /* ------------------------------------------------------------------ */
  const markCourseAsDone = useCallback(async () => {
    if (!content?.id || progress?.completed || savingRef.current) return;

    savingRef.current = true;

    // 1) Optimistic UI update
    setProgress({
      courseId      : content.id,
      completed     : true,
      progress      : 100,
      lastAccessedAt: new Date().toISOString(),
    });

    try {
      // 2) API call – NOTE: le backend attend un booléen
      const serverValue = await api.updateCourseProgress(content.id, true);

      // 3) Sync avec la réponse serveur
      setProgress(prev => {
        const normalised = normaliseProgress(content.id, serverValue);
        return prev?.completed
          ? { ...normalised, completed: true, progress: 100 }
          : normalised;
      });

      updateUserState();
    } catch (err) {
      console.error('[CourseContent] updateCourseProgress failed', err);
    } finally {
      savingRef.current = false;
    }
  }, [content, progress, updateUserState]);

  useEffect(() => {
    if (!bottomInView) return;
    markCourseAsDone();
  }, [bottomInView, content, markCourseAsDone]);

  useEffect(() => {
    if (!(code && courseId)) return;

    /* cas VAE --------------------------------------------------------- */
    if (courseId.startsWith('VAE-')) {
      Promise.all([
        getCourseContent(courseId),
        api
          .getCourseProgress(courseId)
          .then(v => normaliseProgress(courseId, v))
          .catch(() => normaliseProgress(courseId, null)),
      ]).then(([contentData, progressData]) => {
        setContent(contentData);
        setProgress(prev => (prev?.completed ? prev : progressData));
      });
      return;
    }

    /* cours « normaux » ---------------------------------------------- */
    getModules().then((modules) => {
      const currentModule = modules.find((m) => m.code === code);
      if (!currentModule) return;

      getSubModules(currentModule.id).then((subModules) => {
        Promise.all(subModules.map((sm) => getModuleCourses(sm.id))).then(
          (allCourses) => {
            const courses = allCourses.flat();
            const course  = courses.find((c) => slugify(c.title) === courseId);
            if (!course?.id) return;

            setCourseData(course);


            Promise.all([
              getCourseContent(course.id),
              api
                .getCourseProgress(course.id)
                .then(v => normaliseProgress(course.id, v))
                .catch(() => normaliseProgress(course.id, null)),
            ]).then(([contentData, progressData]) => {
              // Check if user can access this course after loading course data
              if (!course.free && !hasActiveSubscription()) {
                navigate('/tarifs');
                return;
              }

              setContent(contentData);
              setProgress(prev => (prev?.completed ? prev : progressData));
            });
          },
        );
      });
    });
  }, [code, courseId]);

  /* ------------------------------------------------------------------ */
  /* render                                                             */
  /* ------------------------------------------------------------------ */
  if (!content) return null;

  return (
    <div
      className="min-h-screen bg-gray-50"
      style={{ fontFamily: font.family }}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className={`flex items-center text-${theme.text} hover:text-${theme.primary} transition-colors`}
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Retour au module
          </button>

          <button
            onClick={markCourseAsDone}
            className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 min-w-[160px] justify-center
              ${progress?.completed
                ? `bg-${theme.primary} text-white hover:opacity-90`
                : `bg-white border-2 border-${theme.primary} text-${theme.primary} hover:bg-${theme.secondary}`
              }`}
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            {progress?.completed ? 'Terminé' : 'Marquer comme terminé'}
          </button>
        </div>

        {/* Content card */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* banner */}
          <div className={`bg-${theme.secondary} p-6`}>
            <div className="flex items-center">
              {content.type === 'video' ? (
                <Video className={`h-6 w-6 text-${theme.text} mr-3`} />
              ) : (
                <FileText className={`h-6 w-6 text-${theme.text} mr-3`} />
              )}
              <h1 className="text-2xl font-bold break-words">{content.title}</h1>
            </div>
          </div>

          {/* body */}
          <div className="p-8">
            {content.type === 'video' ? (
              <div>
                <div className="aspect-video rounded-lg overflow-hidden bg-black mb-8">
                  <iframe
                      src={content.video_url}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                  />
                </div>
                <div className="prose max-w-none">
                  <article className="prose prose-lg max-w-none">
                    <div
                        dangerouslySetInnerHTML={{
                          __html: marked(content.content, {
                            gfm: true,
                            breaks: true,
                            headerIds: false,
                            mangle: false,
                          }),
                        }}
                    />
                  </article>
                </div>
              </div>
            ) : (
                <article className="prose prose-lg max-w-none">
                  <div
                      dangerouslySetInnerHTML={{
                        __html: marked(content.content, {
                          gfm: true,
                          breaks: true,
                          headerIds: false,
                          mangle: false,
                        }),
                  }}
                />
              </article>
            )}
          </div>
        </div>

        {/* Intersection sentinel */}
        <div ref={bottomRef} className="h-1" />

        {/* Comments section for subscribed users */}
        {hasActiveSubscription() && content && (
          <CourseComments courseId={content.id} />
        )}

        <div className="border-t border-gray-100 p-6 mt-6">
          <button
            onClick={() => navigate(-1)}
            className={`flex items-center text-${theme.text} hover:text-${theme.primary} transition-colors`}
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Retour au module
          </button>
        </div>
      </div>
    </div>
  );
}
